import { Fragment, useCallback, useMemo, useState } from "react";

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

import Paper from "@material-ui/core/Paper";
import Fab from "@material-ui/core/Fab";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import AddIcon from "@material-ui/icons/Add";
import ReorderIcon from "@material-ui/icons/Reorder";

import {
  Apartment,
  Service,
  ServiceTypeListItem,
  ServiceTypeListItemType,
} from "../../../graphql/models";

import useListItemsEditorStyle from "./service_list_items_editor.style";
import { ServiceListItemEditor } from "../service_list_item_editor";
import { useReorderServiceListItems } from "../../../graphql/hooks";
import { useSnackbarMessage } from "../../../hooks";
import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
} from "@material-ui/core";
import { useServiceListItemsEditorStore } from "./store";
import { ViewMode } from "./models";

export interface ServiceListItemsEditorProps {
  askCategories?: boolean;
  askRequiredTime?: boolean;
  service: Service;
  isComposable: boolean;
  targetApartment?: Apartment;
  suggestedCategories: {
    it: string[];
    en: string[];
  };
  items: ServiceTypeListItem[];
  type: ServiceTypeListItemType;
  onItemUpdated: () => void;
  onItemDeleted: () => void;
  readonly?: boolean;
}

export function ServiceListItemsEditor(props: ServiceListItemsEditorProps) {
  const {
    askCategories,
    askRequiredTime,
    service,
    isComposable,
    targetApartment,
    suggestedCategories,
    items: initialItems,
    type,
    onItemUpdated,
    onItemDeleted,
    readonly = false,
  } = props;

  const classes = useListItemsEditorStyle();

  const { raiseSuccessSnackbar } = useSnackbarMessage();

  const { currentViewMode, setCurrentViewMode } =
    useServiceListItemsEditorStore();
  const isReoderViewMode = useMemo(
    () => currentViewMode === ViewMode.SortItems,
    [currentViewMode]
  );

  const [items, setItems] = useState<ServiceTypeListItem[]>(initialItems);
  // const { swapItems, loading: swappingItem } = useSwapServiceListItems(() => {
  //   onItemUpdated();
  //   raiseSuccessSnackbar("Ordinamento aggiornato");
  // });

  const { reorderListItems, loading } = useReorderServiceListItems(() => {
    onItemUpdated();
    raiseSuccessSnackbar("Ordinamento aggiornato con successo");
  });

  const addItem = () => {
    setItems([
      ...(items ?? []),
      {
        service: {
          id: service.id,
          name: service.name,
          providedBy: service.providedBy as any,
          type: service.type,
        },
        type,
        order: items.length,
        i18n: {},
        targetApartment: targetApartment
          ? {
              building: {
                id: targetApartment.building.id,
                code: targetApartment.building.name, // TODO fix this
              },
              code: targetApartment.code,
              id: targetApartment.id,
            }
          : void 0,
      },
    ]);
  };

  const triggerUpdateItemPositionAndReorder = useCallback(
    (sourceIndex: number, destinationIndex: number) => {
      const sourceItem = items[sourceIndex];
      reorderListItems(sourceItem, destinationIndex);
    },
    [items, reorderListItems]
  );

  const rowType = useMemo(() => {
    switch (type) {
      case ServiceTypeListItemType.PRODUCT:
        return "Prodotto";
      case ServiceTypeListItemType.SERVICE:
        return "Servizio";
      default:
        return "";
    }
  }, [type]);

  const onDragEnd = ({ destination, source }) => {
    if (!destination || destination.index === undefined) {
      return;
    }

    const { index: destinationIndex } = destination;
    const { index: sourceIndex } = source;

    triggerUpdateItemPositionAndReorder(sourceIndex, destinationIndex);
  };

  return (
    <Paper elevation={3} className={classes.root}>
      <Grid container spacing={4}>
        <Grid item xl={12} md={12} sm={12} xs={12}>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h4">Listino prezzi</Typography>
            {!readonly && (
              <Select
                labelId="service-list-items-editor-view-mode"
                value={currentViewMode}
                onChange={(v) => setCurrentViewMode(Number(v.target.value))}
                variant="outlined"
              >
                <MenuItem value={ViewMode.EditData}>Modifica listino</MenuItem>
                <MenuItem value={ViewMode.SortItems}>
                  Riordina elementi listino
                </MenuItem>
              </Select>
            )}
          </Box>
        </Grid>
        {!isReoderViewMode && (
          <Grid item container xl={12} md={12} sm={12} xs={12} spacing={3}>
            {items?.map((item: ServiceTypeListItem, index) => (
              <Grid item xl={3} md={4} sm={6} xs={12}>
                <ServiceListItemEditor
                  askRequiredTime={askRequiredTime}
                  askCategories={askCategories}
                  isComposable={isComposable}
                  serviceListItem={item}
                  onUpdated={onItemUpdated}
                  onDeleted={onItemDeleted}
                  suggestedCategories={suggestedCategories}
                  readonly={readonly}
                  targetApartment={targetApartment}
                />
              </Grid>
            ))}
          </Grid>
        )}
        {isReoderViewMode && (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable
              droppableId="service-list-items-editor-drop-area"
              direction="vertical"
            >
              {(provided) => (
                <List
                  dense={true}
                  style={{ width: "100%" }}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {items?.map((item: ServiceTypeListItem, index) => (
                    <Draggable draggableId={item.id} index={index}>
                      {(provided, snapshot) => (
                        <Fragment>
                          <ListItem
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                          >
                            <ListItemText
                              primary={item.label}
                              secondary={item.description}
                            />
                            <ListItemSecondaryAction>
                              <ReorderIcon />
                            </ListItemSecondaryAction>
                          </ListItem>
                          <Divider />
                        </Fragment>
                      )}
                    </Draggable>
                  ))}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        )}

        {!readonly && (
          <Grid item sm={3} xs={12}>
            <Fab
              className={classes.fab}
              disabled={loading}
              onClick={() => addItem()}
              variant="extended"
              color="primary"
            >
              <AddIcon className={classes.extendedIcon} />
              Aggiungi {rowType}
            </Fab>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}
